// EditorBar — appears on a post when the caller is an editor (moderator)
// of one of the communities the post has been submitted to. Gives the
// editor the four PP-3/4b/5 verbs inline: Boost, Pin, Return-with-notes,
// plus Approve/Reject when the post is in the pending queue.
//
// Multi-community aware: fetches /api/posts/{id}/editor-context and renders
// one bar per community the caller has editor rights on. Empty context =
// caller isn't an editor of any community this post belongs to → renders
// nothing (self-hides).

import { useCallback, useEffect, useState } from 'react';
import { useApi, ApiError } from '../lib/api';
import { BarSparkline } from './Sparkline';
import type { PostType } from '../types/api';

// Kind labels are the same names used in Paper.tsx's dailyPaperSections
// so the editor can predict which paper section the post lands in.
const KIND_OPTIONS: Array<{ value: PostType; label: string; section: string }> = [
    { value: 'update',    label: 'Update',    section: 'Around the Suburb' },
    { value: 'offer',     label: 'Offer',     section: 'Tonight' },
    { value: 'event',     label: 'Event',     section: 'This Week' },
    { value: 'notice',    label: 'Notice',    section: 'Notices' },
    { value: 'business',  label: 'Business',  section: 'Around the Suburb' },
    { value: 'community', label: 'Community', section: 'Around the Suburb' },
    { value: 'civic',     label: 'Civic',     section: 'Around the Suburb' },
];

interface CommunityCtx {
    id:   string;
    slug: string;
    name: string;
}

interface PostStats {
    recommends:         number;
    comments:           number;
    flags_open:         number;
    engaged_readers:    number;
    hourly_signals_24h: number[];
}

interface Props {
    postID: string;
}

/** Container: fetches editor-context + post stats, renders one InnerBar per community. */
export function EditorBar({ postID }: Props) {
    const api = useApi();
    const [contexts, setContexts] = useState<CommunityCtx[]>([]);
    const [stats, setStats] = useState<PostStats | null>(null);

    useEffect(() => {
        api.get<{ communities: CommunityCtx[] }>(`/api/posts/${postID}/editor-context`)
            .then(r => setContexts(r.communities ?? []))
            .catch(() => setContexts([]));
    }, [api, postID]);

    useEffect(() => {
        if (contexts.length === 0) return; // no bar → no need to fetch stats
        api.get<PostStats>(`/api/posts/${postID}/stats`)
            .then(setStats)
            .catch(() => setStats(null));
    }, [api, postID, contexts.length]);

    if (contexts.length === 0) return null;
    return (
        <>
            {stats && <StatsRow stats={stats} />}
            {contexts.map(c => (
                <InnerBar key={c.id} postID={postID} community={c} />
            ))}
        </>
    );
}

// StatsRow — the decision-support strip above the action bars. Rendered
// once per post (not per community) since the numbers are the same.
function StatsRow({ stats }: { stats: PostStats }) {
    return (
        <div style={statsRowStyle}>
            <span style={statLabel}>Post signals:</span>
            <Chip label="reads"    value={stats.engaged_readers} />
            <Chip label="recs"     value={stats.recommends} />
            <Chip label="comments" value={stats.comments} />
            <Chip label="flags"    value={stats.flags_open} tone={stats.flags_open > 0 ? 'warn' : 'muted'} />
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>24h</span>
                <BarSparkline data={stats.hourly_signals_24h} width={96} height={18} color="#5a4a1a" />
            </span>
        </div>
    );
}

function Chip({ label, value, tone = 'muted' }: { label: string; value: number; tone?: 'muted' | 'warn' }) {
    const color = tone === 'warn' && value > 0 ? '#8b1919' : '#333';
    return (
        <span style={{
            display: 'inline-flex', gap: 4, alignItems: 'baseline',
            padding: '2px 8px', border: '1px solid #d4c9a3', background: '#fdfbf4',
            fontSize: 12, fontFamily: '-apple-system, sans-serif',
        }}>
            <strong style={{ color }}>{value}</strong>
            <span style={{ color: '#888', fontSize: 11 }}>{label}</span>
        </span>
    );
}
const statsRowStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
    padding: '6px 8px', margin: '6px 0 0',
    background: '#faf7ea', border: '1px dashed #d4c9a3', borderRadius: 3,
    fontSize: 12,
};
const statLabel: React.CSSProperties = {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#5a4a1a', marginRight: 6,
};

interface InnerProps {
    postID:    string;
    community: CommunityCtx;
}

interface QuotaResp { remaining: number; max_per_week: number }

function InnerBar({ postID, community }: InnerProps) {
    const api = useApi();
    const [quota, setQuota] = useState<QuotaResp | null>(null);
    const [status, setStatus] = useState<string>('');
    const [busy, setBusy] = useState(false);

    const loadQuota = useCallback(async () => {
        try {
            setQuota(await api.get<QuotaResp>(`/api/communities/${community.slug}/editor/boost-quota`));
        } catch (e) {
            if (!(e instanceof ApiError)) setStatus('quota check failed');
        }
    }, [api, community.slug]);
    useEffect(() => { loadQuota(); }, [loadQuota]);

    async function act(path: string, body?: object, label = 'done') {
        setBusy(true); setStatus('');
        try {
            await api.post(`/api/communities/${community.slug}/editor${path}`, body);
            setStatus('✓ ' + label);
            window.dispatchEvent(new CustomEvent('onhere:post-changed'));
            if (path.includes('/boost')) loadQuota();
        } catch (e) {
            const err = e as Error;
            setStatus('✗ ' + (err.message || 'failed'));
        } finally { setBusy(false); }
    }

    const boostDisabled = busy || (quota?.remaining ?? 0) <= 0;
    return (
        <div className="editor-bar" style={barStyle}>
            <span style={labelStyle}>Editor tools — {community.name}:</span>
            <button type="button" onClick={() => act(`/posts/${postID}/boost`, { reason: '' }, 'boosted')}
                    disabled={boostDisabled} style={btnStyle} title="Lift this post in the paper for 48h">
                ⭐ Boost
                {quota && <em style={quotaStyle}> ({quota.remaining}/{quota.max_per_week})</em>}
            </button>
            <button type="button" onClick={() => act(`/posts/${postID}/pin`, { section: 'lead' }, 'pinned')}
                    disabled={busy} style={btnStyle} title="Pin to community front page for 48h">
                📌 Pin
            </button>
            <button type="button" onClick={() => act(`/posts/${postID}/pin`, { section: 'lead' }, 'unpinned')
                                          .then(() => api.del(`/api/communities/${community.slug}/editor/posts/${postID}/pin`))}
                    disabled={busy} style={btnStyle}>
                Unpin
            </button>
            <button type="button" onClick={() => {
                const note = window.prompt('Send back to author with notes:');
                if (note) act(`/posts/${postID}/return`, { note }, 'returned');
            }} disabled={busy} style={btnStyle} title="Send this post back to the author with a review note">
                ↩ Return
            </button>
            <KindPicker postID={postID} communitySlug={community.slug}
                        onChanged={(v) => setStatus('✓ moved to ' + v)} />
            {status && <span style={{ marginLeft: 12, fontSize: 12, color: status.startsWith('✓') ? 'var(--accent)' : '#8b1919' }}>{status}</span>}
        </div>
    );
}

// KindPicker — lets the editor reclassify a post (Update/Offer/Event/…)
// which decides its paper section (Tonight / This Week / Notices / Around).
// Fetches the current post_type once on mount; PATCHes the community-
// scoped kind endpoint on change. Community-scoped auth: moderator of
// the passed slug can move it; the change is intrinsic to the post so
// it propagates to every community the post appears in.
function KindPicker({ postID, communitySlug, onChanged }: {
    postID: string; communitySlug: string; onChanged: (v: PostType) => void;
}) {
    const api = useApi();
    const [current, setCurrent] = useState<PostType | null>(null);
    const [saving,  setSaving]  = useState(false);

    useEffect(() => {
        api.get<{ post_type: PostType }>(`/api/posts/${postID}`)
            .then(r => setCurrent(r.post_type))
            .catch(() => {});
    }, [api, postID]);

    async function pick(v: PostType) {
        if (!current || v === current || saving) return;
        setSaving(true);
        try {
            await api.patch(`/api/communities/${communitySlug}/posts/${postID}/kind`, { post_type: v });
            setCurrent(v);
            onChanged(v);
            window.dispatchEvent(new CustomEvent('onhere:post-changed'));
        } catch {
            /* status message handled by parent */
        } finally { setSaving(false); }
    }

    if (!current) return null;
    const selected = KIND_OPTIONS.find(k => k.value === current);
    return (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}
               title={selected ? `Paper section: ${selected.section}` : undefined}>
            <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#7a5c00' }}>Kind</span>
            <select value={current} onChange={e => pick(e.target.value as PostType)}
                    disabled={saving}
                    style={{ padding: '2px 4px', border: '1px solid #d4a017', background: '#fffdf6', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer' }}>
                {KIND_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label} → {o.section}</option>
                ))}
            </select>
        </label>
    );
}

const barStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
    padding: '6px 8px', margin: '6px 0 10px',
    background: '#fff8e1', border: '1px dashed #d4a017', borderRadius: 3,
    fontSize: 12, fontFamily: '-apple-system, sans-serif',
};
const labelStyle: React.CSSProperties = {
    fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#7a5c00', marginRight: 6,
};
const btnStyle: React.CSSProperties = {
    padding: '3px 8px', border: '1px solid #d4a017', background: '#fffdf6',
    fontSize: 12, cursor: 'pointer',
};
const quotaStyle: React.CSSProperties = {
    fontStyle: 'normal', color: '#7a5c00', marginLeft: 4,
};
